package nacholab.supplies

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import nacholab.supplies.domain.NetworkRespository
import nacholab.supplies.domain.PreferencesRepository
import nacholab.supplies.domain.ShoppingListItem
import nacholab.supplies.domain.Supply
import nacholab.supplies.network.NetworkMapper
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@OptIn(DelicateCoroutinesApi::class)
class MainViewModel(
    private val preferencesRepository: PreferencesRepository,
    private val networkRespository: NetworkRespository
){
    private val _state = mutableStateOf(MainState.DEFAULT)
    val state: State<MainState> = _state

    private var initialized = false

    fun initialize(){
        if (!initialized) GlobalScope.launch {
            val currentSessionId = preferencesRepository.getSessionId()

            if (currentSessionId.isNotBlank()){
                _state.value = state.value.copy(isAuthenticated = true)
                reloadMySupplies()
            }else{
                _state.value = state.value.copy(isAuthenticated = false)
            }
        }
        initialized = true
    }

//    fun setEditingMode(editingMode: EditingMode){
//        when (editingMode){
//            EditingMode.MARKET_LOCATION -> updateConsumablesByMarketLocation()
//            EditingMode.HOME_LOCATION -> updateConsumablesByHomeLocation()
//            else -> {}
//        }
//        _state.value = state.value.copy(editingMode = editingMode)
//    }

//    fun setSortMode(sortMode: SortMode){
//        when (sortMode){
//            SortMode.MARKET_LOCATION -> updateConsumablesByMarketLocation()
//            SortMode.HOME_LOCATION -> updateConsumablesByHomeLocation()
//            SortMode.NAME -> sortConsumables()
//        }
//        _state.value = state.value.copy(sort = sortMode)
//    }

//    private fun sortConsumables(){
//        _state.value = state.value.copy(
//            supplies = state.value.supplies.sortedBy { it.name }
//        )
//    }

//    private fun updateConsumablesByMarketLocation(){
//        val consumables = state.value.supplies
//        val locations = consumables
//            .map { it.locationAtMarket }
//            .distinct()
//            .sorted()
//
//        val consumablesByMarketLocation = HashMap<String, List<Supply>>()
//        locations.forEach { location ->
//            consumables
//                .filter { it.locationAtMarket == location }
//                .let { consumablesByMarketLocation[location] = it}
//        }
//
//        _state.value = state.value.copy(suppliesByMarketLocation = consumablesByMarketLocation)
//    }

//    private fun updateConsumablesByHomeLocation(){
//        val consumables = state.value.supplies
//        val locations = consumables
//            .map { it.locationAtHome }
//            .distinct()
//            .sorted()
//
//        val consumablesByHomeLocation = HashMap<String, List<Supply>>()
//        locations.forEach { location ->
//            consumables
//                .filter { it.locationAtHome == location }
//                .let { consumablesByHomeLocation[location] = it}
//        }
//
//        _state.value = state.value.copy(suppliesByHomeLocation = consumablesByHomeLocation)
//    }

    fun setEditingConsumable(consumable: Supply?){
        _state.value = state.value.copy(editingConsumable = consumable)
    }

    fun increaseRequiredStock(id: String){
        GlobalScope.launch {
            val updatedConsumables = state
                .value
                .supplies
                .map {
                    if (it.id == id)
                        it.copy(requiredStock = it.requiredStock + 1)
                    else it
                }

            _state.value = state.value.copy(supplies = updatedConsumables)
            uploadSupplies()
        }
    }

    fun reduceRequiredStock(id: String){
        GlobalScope.launch {
            val updatedConsumables = state
                .value
                .supplies
                .map {
                    if (it.id == id)
                        it.copy(requiredStock = it.requiredStock - 1)
                    else it
                }

            _state.value = state.value.copy(supplies = updatedConsumables)
            uploadSupplies()
        }
    }

    fun increaseCurrentStock(id: String){
        GlobalScope.launch {
            val updatedConsumables = state
                .value
                .supplies
                .map {
                    if (it.id == id)
                        it.copy(currentStock = it.currentStock + 1)
                    else it
                }

            _state.value = state.value.copy(supplies = updatedConsumables)
            uploadSupplies()
        }
    }

    fun reduceCurrentStock(id: String){
        GlobalScope.launch {
            val updatedConsumables = state
                .value
                .supplies
                .map {
                    if (it.id == id)
                        it.copy(currentStock = it.currentStock - 1)
                    else it
                }

            _state.value = state.value.copy(supplies = updatedConsumables)
            uploadSupplies()
        }
    }

    fun markItemAsPicked(id: String, picked: Boolean){
        val currentShoppingList = state.value.shoppingList
        val consumableLocation = state.value.supplies.find { it.id == id }?.locationAtMarket

        val updatedShoppingList = HashMap<String, List<ShoppingListItem>>()

        currentShoppingList.keys.forEach { location ->
            if (consumableLocation == location){
                currentShoppingList[location]?.map {
                    if (it.consumable.id == id) it.copy(picked = picked)
                    else it
                }?.let { upd -> updatedShoppingList[location] = upd}
            }else currentShoppingList[location]?.let { updatedShoppingList[location] = it }
        }

        _state.value = state.value.copy(shoppingList = updatedShoppingList)
    }

    fun updateDBFromShoppinglist(){
        val flatShoppingList = arrayListOf<ShoppingListItem>()

        state.value.shoppingList.forEach {
            flatShoppingList.addAll(it.value)
        }

        updateDBFromShoppinglist(flatShoppingList)
        uploadSupplies()
    }

    private fun updateDBFromShoppinglist(shoppingList: List<ShoppingListItem>){
        GlobalScope.launch {
            val updatedConsumables = state
                .value
                .supplies
                .map {
                    if (shoppingList.find { s -> s.consumable.id == it.id }?.picked == true)
                        it.copy(currentStock = it.requiredStock)
                    else it
                }

            _state.value = state.value.copy(supplies = updatedConsumables)
        }
    }

    fun buildShoppingList(){
        _state.value = state.value.copy(
            shoppingList = buildShoppingList(_state.value.supplies)
        )
    }

    fun buildShoppingList(consumables: List<Supply>): HashMap<String, List<ShoppingListItem>> {
        val missingConsumables = consumables
            .filter {
                (it.requiredStock > 0 && it.currentStock < it.requiredStock) ||
                        (it.requiredStock == -1 && it.currentStock != -1)
            }

        val marketLocations = missingConsumables
            .map { it.locationAtMarket }
            .distinct()
            .sorted()

        val shoppingList = HashMap<String, List<ShoppingListItem>>()
        marketLocations.forEach { location ->
            shoppingList[location] = missingConsumables
                .filter { it.locationAtMarket == location }
                .map { ShoppingListItem(it, false) }
        }

        return shoppingList
    }

    fun updateConsumable(
        id: String,
        name: String,
        homeLocation: String,
        marketLocation: String,
        currentStock: Int,
        requiredStock: Int
    ) {
        GlobalScope.launch {
            val updatedConsumables = state.value.supplies.map {
                if (it.id == id) it.copy(
                    name = name,
                    locationAtHome = homeLocation,
                    locationAtMarket = marketLocation,
                    currentStock = currentStock,
                    requiredStock = requiredStock
                ) else it
            }


            _state.value = state.value.copy(supplies = updatedConsumables)
            uploadSupplies()
        }
    }

    @OptIn(ExperimentalUuidApi::class)
    fun addConsumable(
        name: String,
        homeLocation: String,
        marketLocation: String,
        currentStock: Int,
        requiredStock: Int
    ) {
        GlobalScope.launch {
            val mutableConsumables = state.value.supplies.toMutableList()
            mutableConsumables.add(
                Supply(
                    id = Uuid.random().toString(),
                    name = name,
                    locationAtHome = homeLocation,
                    locationAtMarket = marketLocation,
                    currentStock = currentStock,
                    requiredStock = requiredStock
                )
            )
            _state.value = state.value.copy(supplies = mutableConsumables)
            uploadSupplies()
        }
    }

//    fun importDBFromFile(file: File) {
//        GlobalScope.launch {
//            dbRepository.importFromFile(file)
//            val newSupplies = dbRepository.loadCurrentDB()
//            _state.value = state.value.copy(supplies = newSupplies.map { SupplyMapper.buildFrom(it) })
//            updateConsumablesByHomeLocation()
//            updateConsumablesByMarketLocation()
//            uploadSupplies()
//        }
//    }

//    fun exportDBTofile() {
//        viewModelScope.launch {
//            val db = dbRepository.exportToJSON()
//            _state.value = state.value.copy(exportDBToFile = db)
//        }
//    }

//    fun exportDBToFileSignalClear(){
//        _state.value = state.value.copy(exportDBToFile = null)
//    }

    fun deleteConsumable(id: String) {
        GlobalScope.launch {
            val updatedConsumables = state.value.supplies.filter { it.id != id }
            _state.value = state.value.copy(supplies = updatedConsumables)
            uploadSupplies()
        }
    }

    fun authenticateUser(email: String) {
        if (!email.contains("@") || !email.contains(".") || email.length < 8 ){
            _state.value = state.value.copy(
                waitingForCodeVerification = false,
                isAuthenticationLoading = false,
                authenticationError = "Invalid email address",
                currentAuthenticatingEmail = null
            )
        }else {
            if (!state.value.isAuthenticationLoading) {
                _state.value = state.value.copy(
                    waitingForCodeVerification = false,
                    isAuthenticationLoading = true,
                    authenticationError = null,
                    currentAuthenticatingEmail = null,
                )
                GlobalScope.launch {
                    try{
                        val authenticationResult = networkRespository.authenticateUser(email)
                        if (authenticationResult) {
                            _state.value = state.value.copy(
                                waitingForCodeVerification = true,
                                isAuthenticationLoading = false,
                                authenticationError = null,
                                currentAuthenticatingEmail = email,
                            )
                        } else {
                            _state.value = state.value.copy(
                                waitingForCodeVerification = false,
                                isAuthenticationLoading = false,
                                authenticationError = "Ni idea, algo salió mal",
                                currentAuthenticatingEmail = null,
                            )
                        }
                    }catch (e: Exception){
                        _state.value = state.value.copy(
                            waitingForCodeVerification = false,
                            isAuthenticationLoading = false,
                            authenticationError = e.message + e.stackTraceToString(),
                            currentAuthenticatingEmail = null,
                        )
                    }
                }
            }
        }
    }

    fun verifyCode(code: String){
        if (code.length != 6){
            _state.value = state.value.copy(
                waitingForCodeVerification = true,
                isAuthenticationLoading = false,
                authenticationError = "Invalid verification code"
            )
        }else {
            if (!state.value.isAuthenticationLoading) {
                _state.value = state.value.copy(
                    waitingForCodeVerification = true,
                    isAuthenticationLoading = true,
                    authenticationError = null
                )
                GlobalScope.launch {
                    try {
                        state
                            .value
                            .currentAuthenticatingEmail
                            ?.let { email ->
                                val createdSession = networkRespository.createSession(email, code)

                                if (createdSession!=null){
                                    preferencesRepository.storeSessionId(createdSession.id)
                                    reloadMySupplies()
                                    _state.value = state.value.copy(
                                        isAuthenticated = true,
                                        waitingForCodeVerification = false,
                                        isAuthenticationLoading = false,
                                        authenticationError = null,
                                        suppliesLoading = true,
                                        currentAuthenticatingEmail = null,
                                    )
                                }else{
                                    _state.value = state.value.copy(
                                        waitingForCodeVerification = true,
                                        isAuthenticationLoading = false,
                                        authenticationError = "Invalid code!"
                                    )
                                }
                            }
                    }catch (e: Exception){
                        _state.value = state.value.copy(
                            waitingForCodeVerification = true,
                            isAuthenticationLoading = false,
                            authenticationError = e.message + e.stackTraceToString()
                        )
                    }
                }
            }
        }
    }

    fun clearAuthenticationError(){
        _state.value = state.value.copy(authenticationError = null)
    }

    fun reloadMySupplies(){
        _state.value = state.value.copy(suppliesLoading = true, suppliesLoadingError = null)

        GlobalScope.launch {
            try {
                val apiSupplies = networkRespository.getMySupplies()
                val supplies = apiSupplies.map { NetworkMapper.buildFrom(it) }

                _state.value = state.value.copy(
                    supplies = supplies,
                    suppliesLoading = false,
                    suppliesLoadingError = null
                )
            }catch (e: Exception){
                _state.value = state.value.copy(
                    suppliesLoading = false,
                    suppliesLoadingError = e.message + e.stackTraceToString()
                )
            }
        }
    }
    fun toggleEditMode(){
        if (!state.value.editingMode){
            val currentNames = state.value.nameFields.toMutableMap()
            val currentCurrentStocks = state.value.currentStockFields.toMutableMap()
            val currentRequiredStocks = state.value.requiredStockFields.toMutableMap()
            val currentHomes = state.value.homeFields.toMutableMap()
            val currentMarkets = state.value.marketFields.toMutableMap()

            state.value.supplies.forEach {
                currentNames[it.id] = it.name
                currentCurrentStocks[it.id] = it.currentStock
                currentRequiredStocks[it.id] = it.requiredStock
                currentHomes[it.id] = it.locationAtHome
                currentMarkets[it.id] = it.locationAtMarket
            }

            _state.value = state.value.copy(
                nameFields = currentNames,
                currentStockFields = currentCurrentStocks,
                requiredStockFields = currentRequiredStocks,
                homeFields = currentHomes,
                marketFields = currentMarkets,
                editingMode = true,
                changesMade = false,
                changesMadeFields = mapOf()
            )
        } else{
            _state.value = state.value.copy(editingMode = false, changesMadeFields = mapOf())
            if (state.value.changesMade) reloadMySupplies()
        }
    }

    private fun uploadSupplies(){
        _state.value = state.value.copy(savingEdit = true, suppliesLoadingError = null)

        GlobalScope.launch {
            try {
                networkRespository.saveMySupplies(state.value.supplies.map {
                    NetworkMapper.buildFrom(
                        it
                    )
                })
                _state.value = state.value.copy(savingEdit = false, suppliesLoadingError = null)
            }catch (e: Exception){
                _state.value = state.value.copy(savingEdit = false, suppliesLoadingError = e.message + e.stackTraceToString())
            }
        }
    }

    fun clearSuppliesLoadingError(){
        _state.value = state.value.copy(suppliesLoadingError = null)
    }

    fun logout() {
        GlobalScope.launch {
            preferencesRepository.storeSessionId("")
            _state.value = MainState.DEFAULT
        }
    }

    private fun setChangesMade(supplyId: String, field: String){
        val changesMade = state.value.changesMadeFields.toMutableMap()
        val key = "$field-$supplyId"
        changesMade[key] = true
        _state.value = state.value.copy(changesMadeFields = changesMade)
    }

    fun updateNameField(supplyId: String, text: String){
        val currentNames = state.value.nameFields.toMutableMap()
        if (currentNames[supplyId] != text){
            currentNames[supplyId] = text
            setChangesMade(supplyId, "name")
            _state.value = state.value.copy(
                nameFields = currentNames,
                changesMade = true
            )
        }
    }

    fun updateCurrentStockField(supplyId: String, number: Int){
        val current = state.value.currentStockFields.toMutableMap()
        if (current[supplyId] != number){
            current[supplyId] = number
            _state.value = state.value.copy(currentStockFields = current, changesMade = true)
            GlobalScope.launch { updateSupply(supplyId) }
        }
    }

    fun updateRequiredStockField(supplyId: String, number: Int){
        val current = state.value.requiredStockFields.toMutableMap()
        if (current[supplyId] != number){
            current[supplyId] = number
            _state.value = state.value.copy(requiredStockFields = current, changesMade = true)
            GlobalScope.launch { updateSupply(supplyId) }
        }
    }

    fun updateHomeField(supplyId: String, text: String){
        val current = state.value.homeFields.toMutableMap()
        if (current[supplyId] != text) {
            current[supplyId] = text
            setChangesMade(supplyId, "home")
            _state.value = state.value.copy(homeFields = current, changesMade = true)
        }
    }

    fun updateMarketField(supplyId: String, text: String){
        val current = state.value.marketFields.toMutableMap()
        if (current[supplyId] != text) {
            current[supplyId] = text
            setChangesMade(supplyId, "market")
            _state.value = state.value.copy(marketFields = current, changesMade = true)
        }
    }

    fun startEditingName(id: String) = startEditing("name", id)
    fun endEditingName(id: String) = endEditing("name", id)
    fun startEditingHome(id: String) = startEditing("home", id)
    fun endEditingHome(id: String) = endEditing("home", id)
    fun startEditingMarket(id: String) = startEditing("market", id)
    fun endEditingMarket(id: String) = endEditing("market", id)

    private fun startEditing(field: String, supplyId: String){
        console.log("start Editing $field - $supplyId")
        val changesMade = state.value.changesMadeFields.toMutableMap()
        changesMade["$field-$supplyId"] = false
        _state.value = state.value.copy(changesMadeFields = changesMade)
    }

    private fun endEditing(field: String, supplyId: String){
        val changesMade = state.value.changesMadeFields.toMutableMap()
        val key = "$field-$supplyId"
        val fieldChange = changesMade[key] == true

        console.log("end Editing $field - $supplyId - $fieldChange")
        if (fieldChange){
            _state.value = state.value.copy(savingEdit = true)

            GlobalScope.launch {
                try{
                    updateSupply(supplyId)
                    changesMade[key] = false
                    _state.value = state.value.copy(savingEdit = false, suppliesLoadingError = null)
                    _state.value = state.value.copy(
                        savingEdit = false,
                        changesMadeFields = changesMade,
                        suppliesLoadingError = null
                    )
                }catch (e: Exception){
                    _state.value = state.value.copy(
                        savingEdit = false,
                        suppliesLoadingError = e.message + e.stackTraceToString()
                    )
                }
            }
        }
    }

    private suspend fun updateSupply(id: String) = state
        .value
        .supplies
        .map {
            if (it.id == id){
                Supply(
                    id = id,
                    name = state.value.nameFields[id]?.trim()?:"",
                    requiredStock = state.value.requiredStockFields[id]?:0,
                    currentStock = state.value.currentStockFields[id]?:0,
                    locationAtHome = state.value.homeFields[id]?.trim()?:"",
                    locationAtMarket = state.value.marketFields[id]?.trim()?:"",
                )
            }else it
        }
        .map { NetworkMapper.buildFrom(it) }
        .let { networkRespository.saveMySupplies(it) }

    fun sortByName() = sort("name")
    fun sortByHome() = sort("home")
    fun sortByMarket() = sort("market")

    private fun sort(field: String){
        state
            .value
            .supplies
            .sortedBy {
                when (field){
                    "name" -> it.name
                    "home" -> it.locationAtHome
                    "market" -> it.locationAtMarket
                    else -> it.name
                }
            }
            .let {
                _state.value = state.value.copy(supplies = it)
            }


    }

    fun initAddSupply(){
        _state.value = state.value.copy(
            showAddSupplyDialog = true,
            addSupplyName = "",
            addSupplyRequiredStock = 0,
            addSupplyCurrentStock = 0,
            addSupplyLocationAtHome = "",
            addSupplyLocationAtMarket = ""
        )
    }

    fun cancelAddSupply(){
        _state.value = state.value.copy(showAddSupplyDialog = false)
    }

    @OptIn(ExperimentalUuidApi::class)
    fun saveCurrentlyAddingSupply(){
        _state.value = state.value.copy(savingEdit = true)

        GlobalScope.launch {
            try {
                state
                    .value
                    .supplies
                    .toMutableList()
                    .apply {
                        add(
                            Supply(
                                id = Uuid.random().toString(),
                                name = state.value.addSupplyName,
                                requiredStock = state.value.addSupplyRequiredStock,
                                currentStock = state.value.addSupplyCurrentStock,
                                locationAtHome = state.value.addSupplyLocationAtHome,
                                locationAtMarket = state.value.addSupplyLocationAtMarket
                            )
                        )
                    }
                    .map { NetworkMapper.buildFrom(it) }
                    .let { networkRespository.saveMySupplies(it) }

                _state.value = state.value.copy(savingEdit = false, suppliesLoadingError = null, showAddSupplyDialog = false)
                reloadMySupplies()
            } catch (e: Exception) {
                _state.value = state.value.copy(savingEdit = false, suppliesLoadingError = e.message)
            }
        }
    }

    fun updateCreatingSupplyName(text: String){
        _state.value = state.value.copy(addSupplyName = text)
    }

    fun updateCreatingSupplyLocationAtHome(text: String){
        _state.value = state.value.copy(addSupplyLocationAtHome = text)
    }

    fun updateCreatingSupplyLocationAtMarket(text: String){
        _state.value = state.value.copy(addSupplyLocationAtMarket = text)
    }

    fun updateCreatingSupplyCurrentStock(number: Int){
        _state.value = state.value.copy(addSupplyCurrentStock = number)
    }

    fun updateCreatingSupplyRequiredStock(number: Int){
        _state.value = state.value.copy(addSupplyRequiredStock = number)
    }
}